import { useEffect, useMemo, useState } from 'react';
import { useInterval } from 'usehooks-ts';

import usePublicApiQuery from '@/hooks/api/usePublicApiQuery';
import { calculateDuration, calculateProgressPercentage, isBuildNotFinished, PublicApiBuild } from '@/models/Build';
import { SimpleErrorResponse } from '@/models/Error';
import { PublicApiDataResponse, PublicApiPagedResponse } from '@/models/network';

import useBuildDetailsPageStore from '../BuildDetailsPage.store';

const REFETCH_INTERVAL = 5000;

const useBuildDetails = (
  appSlug: string,
  buildSlug: string,
  isViewedAsPublicPage: boolean,
  initialBuild?: PublicApiBuild,
  options?: { isEnabled: boolean },
) => {
  const [build, setBuild] = useState(initialBuild);

  const shouldEnable =
    options?.isEnabled !== false && (!build || isBuildNotFinished(build) || build.slug !== buildSlug);

  const { data, isError, isLoading } = usePublicApiQuery<PublicApiDataResponse<PublicApiBuild>, SimpleErrorResponse>(
    ['build', appSlug, buildSlug],
    `/v0.1/apps/${appSlug}/builds/${buildSlug}`,
    {
      enabled: shouldEnable,
      refetchInterval: shouldEnable ? REFETCH_INTERVAL : false,
      refetchIntervalInBackground: true,
      requestConfig: {
        publicAccess: isViewedAsPublicPage,
      },
    },
  );

  useEffect(() => {
    if (data) {
      setBuild(data.data);
    }
  }, [data]);

  return useMemo(() => {
    return {
      build,
      isError,
      isLoading,
    };
  }, [build, isError, isLoading]);
};

export const useBuildProgress = (
  appSlug: string,
  isViewedAsPublicPage: boolean,
): { progress: number; previousBuildDuration?: number } => {
  const build = useBuildDetailsPageStore((state) => state.build);
  const hasNotFinished = isBuildNotFinished(build);
  const [progress, setProgress] = useState(0);
  const [previousBuildDuration, setPreviousBuildDuration] = useState<number | undefined>(undefined);

  const { data: buildsApiResponse } = usePublicApiQuery<PublicApiPagedResponse<PublicApiBuild>, SimpleErrorResponse>(
    ['lastSuccessfulBuild'],
    `/v0.1/apps/${appSlug}/builds?status=1&workflow=${build.triggeredWorkflow}&limit=1`,
    {
      enabled: hasNotFinished,
      requestConfig: {
        publicAccess: isViewedAsPublicPage,
      },
    },
  );

  function update() {
    const actualBuildDuration = calculateDuration(build);
    const lastSuccessfulBuild = buildsApiResponse?.data[0];

    if (lastSuccessfulBuild) {
      const calculatedPreviousBuildDuration = calculateDuration(lastSuccessfulBuild);
      setProgress(calculateProgressPercentage(actualBuildDuration, calculatedPreviousBuildDuration));
      setPreviousBuildDuration(calculatedPreviousBuildDuration);
    }
  }

  useInterval(update, hasNotFinished ? 1000 : null);

  return { progress, previousBuildDuration };
};

export default useBuildDetails;
